import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  pdf,
} from "@react-pdf/renderer";

import { useState, useEffect } from "react";
import axios from "axios";
import BASE_URL from "../../../assets/global/url";
import {
  formatToTwoDecimal,
  getCurrencySymbol,
} from "../../../utils/numberFormatter";

import NunitoSemiBold from "../../../assets/fonts/Nunito/Nunito-SemiBold.ttf";
import CommissionPDFSection from "./components/CommissionPDFSection";
import PDFPreviewFrame from "../../../components/PDFPreviewFrame";

Font.register({
  family: "Nunito",
  src: NunitoSemiBold,
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 20,
  },
  header: {
    fontSize: 16,
    marginBottom: 10,
    textAlign: "center",
    fontWeight: "bold",
  },
  currencySymbol: {
    fontSize: 7,
    fontFamily: "Nunito",
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginTop: 10,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
    minHeight: 15,
  },
  tableCol: {
    width: "8.33%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 2,
  },
  tableColWide: {
    width: "10%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 2,
  },
  tableCell: {
    margin: "auto",
    fontSize: 7,
    textAlign: "center",
  },
  tableHeadCell: {
    margin: "auto",
    fontSize: 7,
    fontWeight: 800,
    textAlign: "center",
  },
  infoSection: {
    marginBottom: 10,
    fontSize: 9,
  },
  row: {
    flexDirection: "row",
    marginBottom: 5,
  },
  column: {
    marginRight: 10,
  },
  label: {
    fontSize: 9,
    color: "#666666",
    marginBottom: 2,
  },
  value: {
    fontSize: 9,
    padding: 2,
    borderBottom: "1px solid black",
    minHeight: 12,
  },

  totalContainer: {
    marginTop: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 9,
  },
  totalCard: {
    width: "30%",
    padding: 5,
    gap: 2,
  },
  totalCardRightSide: {
    width: "65%",
    padding: 5,
    gap: 2,
    alignItems: "flex-end",
  },
  summaryColumn: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 2,
    width: "100%",
    alignItems: "center",
  },
  subCard: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    width: "100%",
    marginBottom: 2,
  },
  summaryLabel: {
    fontSize: 9,
    color: "#000",
    marginRight: 5,
  },
  summaryValue: {
    fontSize: 9,
    width: 80,
    textAlign: "right",
    borderBottom: "1px solid black",
    paddingBottom: 1,
  },
  tableHead: {
    backgroundColor: "#E9ECEF",
    padding: 2,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 8,
    color: "#000",
  },
});

// Loads everything the payable PDF needs in parallel and returns one object,
// so the PDF is laid out once instead of once per field. payablePDF/fetch
// already includes the currency, so the full currency list is not needed.
const fetchPayablePDFData = async (id) => {
  const [payableRes, productRes, feesRes] = await Promise.all([
    axios.get(BASE_URL + "/payablePDF/fetch", { params: { id } }),
    axios.get(BASE_URL + "/payablePDF/payableProduct", { params: { id } }),
    axios.get(BASE_URL + "/payablePDF/payableFees", { params: { id } }),
  ]);

  const data = payableRes.data.isFetch[0];
  const payableProducts = productRes.data;
  const fetchPayableFees = feesRes.data;

  const totalWeighted = payableProducts.reduce(
    (sum, item) => sum + (item.weighted || 0),
    0,
  );
  const totalWeightInKilo = payableProducts.reduce(
    (sum, item) => sum + (item.weight || 0),
    0,
  );

  const { totalReturnedAmount, totalMoisture, totalReturnedQty } =
    payableProducts.reduce(
      (acc, item) => {
        const weight = parseFloat(item.weight || 0);
        const unitPrice = parseFloat(item.unitPrice || 0);
        const net_weight = parseFloat(item.net_weight || 0);
        const static_net_weight = parseFloat(item.static_net_weight || 0);
        const returnedWeight = static_net_weight - net_weight;

        acc.totalReturnedAmount += returnedWeight * unitPrice;
        acc.totalReturnedQty += returnedWeight;
        acc.totalMoisture += (weight - net_weight) * unitPrice;
        return acc;
      },
      { totalReturnedAmount: 0, totalMoisture: 0, totalReturnedQty: 0 },
    );

  const toNumber = (value) =>
    parseFloat(String(value).replace(/,/g, "")) || 0;
  const totalMoistureInKilo = payableProducts.reduce(
    (total, item) => total + (toNumber(item.weight) - toNumber(item.net_weight)),
    0,
  );
  const totalNetWeightInKilo = payableProducts.reduce(
    (total, item) => total + toNumber(item.net_weight),
    0,
  );

  return {
    transaction_id: data.transaction_id,
    totalPrice: data.totalPrice || 0,
    purchaseDate: data.purchaseDate,
    vendorName: data.vendor?.company_name,
    warehouseName: data.warehouse?.name,
    weighingFee: data.weighing_fee || 0,
    discountValue: data.discount_value || 0,
    payableDiscountType: data.isPercent_Discount == 1 ? "%" : "currency",
    currencySymbol: getCurrencySymbol(
      data.currency ? [data.currency] : [],
      data.currency?.id,
    ),
    status: data.status,
    vatPercent: data.vat_percent,
    taxRate: data.tax_rate || 0,
    isReOpenAdded: data.is_re_open_added || false,
    isSpecialTaxApplied: data.isSpecialTax || false,
    commissions: data.payable_commissions,
    payableProducts,
    fetchPayableFees,
    totalFeeAmount: fetchPayableFees.reduce(
      (sum, item) => sum + (item.fee_amount || 0),
      0,
    ),
    totalWeighted,
    totalWeightInKilo,
    totalMoisture,
    totalReturnedAmount,
    totalReturnedQty,
    totalMoistureInKilo,
    totalNetWeightInKilo,
    totalNetWeight: totalWeighted - totalMoisture,
    totalItemDiscount: payableProducts.reduce(
      (total, item) => total + getItemDiscountAmount(item),
      0,
    ),
  };
};

// Per-product discount amount, same rule as the PO form: base is
// net_weight × unitPrice, "%" or "fixed", capped to the row's base.
const getItemDiscountAmount = (item) => {
  const base =
    (parseFloat(item.net_weight) || 0) * (parseFloat(item.unitPrice) || 0);
  const value = parseFloat(item.discount) || 0;
  const amount = item.discount_type === "fixed" ? value : (value / 100) * base;
  return Math.min(Math.max(amount, 0), base);
};

// Fetches the payable and renders the PDF to a blob URL exactly once per open.
// PDFViewer / PDFDownloadLink re-render the whole PDF (and reload the iframe)
// on every parent re-render, which makes the preview blink, so the caller
// shows the returned url with PDFPreviewFrame and a plain <a download>.
export const usePayablePDF = (id, enabled = true) => {
  const [state, setState] = useState({
    url: null,
    transactionId: "",
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!enabled || !id) return;
    let cancelled = false;
    let createdUrl = null;

    setState({ url: null, transactionId: "", loading: true, error: null });

    const load = async () => {
      try {
        const pdfData = await fetchPayablePDFData(id);
        if (cancelled) return;

        const blob = await pdf(<PayablePDFDocument data={pdfData} />).toBlob();
        if (cancelled) return;

        createdUrl = URL.createObjectURL(blob);
        setState({
          url: createdUrl,
          transactionId: pdfData.transaction_id,
          loading: false,
          error: null,
        });
      } catch (err) {
        console.log(err);
        if (!cancelled) {
          setState({ url: null, transactionId: "", loading: false, error: err });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      // Drop the revoked url so a reopened modal shows the spinner, not a
      // broken frame, until the new PDF is ready.
      setState({ url: null, transactionId: "", loading: false, error: null });
    };
  }, [id, enabled]);

  return state;
};

export const PayablePDFFrame = (props) => (
  <PDFPreviewFrame title="Payable" {...props} />
);

// Pure document built from the data prepared in fetchPayablePDFData.
const PayablePDFDocument = ({ data }) => {
  const {
    transaction_id,
    totalPrice,
    purchaseDate,
    vendorName,
    warehouseName,
    weighingFee,
    discountValue,
    payableDiscountType,
    currencySymbol,
    status,
    vatPercent,
    taxRate,
    isReOpenAdded,
    isSpecialTaxApplied,
    commissions,
    payableProducts,
    fetchPayableFees,
    totalFeeAmount,
    totalWeighted,
    totalWeightInKilo,
    totalMoisture,
    totalReturnedAmount,
    totalReturnedQty,
    totalMoistureInKilo,
    totalNetWeightInKilo,
    totalNetWeight,
    totalItemDiscount,
  } = data;
  const today = new Date().toLocaleDateString();

  // ─── Product subtotal (no fees) ────────────────────────────────────────────
  const productSubtotal = totalWeighted - totalMoisture;

  // ─── Subtotal after per-product discounts ──────────────────────────────────
  const discountedSubtotal = productSubtotal - totalItemDiscount;

  // ─── Discount — handles both % and currency ───────────────────────────────
  const discountCalculation =
    payableDiscountType === "currency"
      ? parseFloat(discountValue || 0)
      : (parseFloat(discountValue || 0) / 100) * discountedSubtotal;

  // ─── Gross Amount ──────────────────────────────────────────────────────────
  const grossAmount = discountedSubtotal - discountCalculation;

  const isSpecialTaxApplicable = isSpecialTaxApplied ? grossAmount * 0.1088 : 0;

  // ─── VAT — basis is grossAmount (products only) ────────────────────────────
  const vatAmount = (parseFloat(vatPercent || 0) / 100) * grossAmount;

  // ─── Withholding Tax — basis is grossAmount (products only) ───────────────
  const taxAmount = (parseFloat(taxRate || 0) / 100) * grossAmount;

  // ─── Non-taxable fees added last ───────────────────────────────────────────
  const totalAmount =
    grossAmount +
    isSpecialTaxApplicable +
    vatAmount -
    taxAmount -
    weighingFee -
    totalFeeAmount -
    totalReturnedAmount;

  return (
    <Document title={`Payable - ${transaction_id}`}>
      <Page size="A4" style={styles.page} orientation="portrait">
        <Text style={styles.header}>Payment Overview</Text>

        <View style={styles.infoSection}>
          <View style={styles.row}>
            {/* <View style={styles.column}>
              <Text style={styles.label}>Supplier No: </Text>
              <Text style={styles.value}>{vendorId}</Text>
            </View> */}
            <View style={{ ...styles.column, flex: 1 }}>
              <Text style={styles.label}>Supplier name: </Text>
              <Text style={styles.value}>{vendorName}</Text>
            </View>
            <View style={{ ...styles.column, flex: 1 }}>
              <Text style={styles.label}>Date: </Text>
              <Text style={styles.value}>{purchaseDate}</Text>
            </View>
            <View style={{ ...styles.column, flex: 1 }}>
              <Text style={styles.label}>Number: </Text>
              <Text style={styles.value}>{transaction_id}</Text>
            </View>
          </View>
        </View>

        {/* <View style={styles.infoSection}>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Transaction ID</Text>
              <Text style={styles.value}>{transaction_id}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Amount to Pay </Text>
              <Text style={styles.value}>
                {totalPrice.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Currency</Text>
              <Text style={styles.value}>{currency_name}</Text>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Foreign Type</Text>
              <Text style={styles.value}>
                {domestic_type ? `${domestic_type.toUpperCase()} OVERSEAS` : ""}
              </Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Purchase Date</Text>
              <Text style={styles.value}>{purchaseDate}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Due Date</Text>
              <Text style={styles.value}>{due_date}</Text>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Vendor</Text>
              <Text style={styles.value}>{vendorName}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Email</Text>
              <Text style={styles.value}>{email}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Receiving Warehouse</Text>
              <Text style={styles.value}>{warehouseName}</Text>
            </View>
          </View>
        </View> */}

        <View style={styles.table}>
          <View style={[styles.tableRow]}>
            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  {
                    fontWeight: 600,
                    textAlign: "left",
                  },
                ]}
              >
                Receiving {"\n"}Warehouse
              </Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Product Name
              </Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Product Code
              </Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Unit
              </Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Weighed QTY
              </Text>
            </View>

            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Moisture
              </Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                N.W.
              </Text>
            </View>
            {status === "Approved" && (
              <View
                style={[
                  styles.tableCol,
                  styles.tableHead,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text
                  style={[
                    styles.tableCell,
                    { fontWeight: 600, textAlign: "left" },
                  ]}
                >
                  Returned Qty.
                </Text>
              </View>
            )}

            {status === "Approved" && (
              <View
                style={[
                  styles.tableCol,
                  styles.tableHead,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text
                  style={[
                    styles.tableCell,
                    { fontWeight: 600, textAlign: "left" },
                  ]}
                >
                  T.N.W
                </Text>
              </View>
            )}

            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Unit Price
              </Text>
            </View>

            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Discount
              </Text>
            </View>

            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                !status || status !== "Approved" ? styles.tableColWide : {},
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Subtotal
              </Text>
            </View>
          </View>

          {payableProducts.map((item, index) => (
            <View style={styles.tableRow} key={index}>
              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {warehouseName}
                </Text>
              </View>
              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {item.product_tag_vendor?.product_list?.product_name || ""}
                </Text>
              </View>
              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {item.product_tag_vendor?.product_list?.product_code || ""}
                </Text>
              </View>
              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {item.product_tag_vendor?.product_list?.unit_of_measure || ""}
                </Text>
              </View>
              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {formatToTwoDecimal(item.weight)}
                </Text>
              </View>

              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {formatToTwoDecimal(item.moisture)}%
                </Text>
              </View>
              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {item.weight !== undefined && item.weight !== null
                    ? formatToTwoDecimal(
                        // Math.trunc(
                        //   item.weight * (1 - (item.moisture || 0) / 100),
                        // ),
                        item.static_net_weight,
                      )
                    : ""}
                </Text>
              </View>
              {/* returned qty */}
              {status === "Approved" && (
                <View
                  style={[
                    styles.tableCol,
                    !status || status !== "Approved" ? styles.tableColWide : {},
                  ]}
                >
                  <Text style={[styles.tableCell, { textAlign: "left" }]}>
                    {formatToTwoDecimal(
                      item.static_net_weight - item.net_weight,
                    )}
                  </Text>
                </View>
              )}

              {/* total net weight (net weght - returned qty) */}
              {status === "Approved" && (
                <View
                  style={[
                    styles.tableCol,
                    !status || status !== "Approved" ? styles.tableColWide : {},
                  ]}
                >
                  <Text style={[styles.tableCell, { textAlign: "left" }]}>
                    {formatToTwoDecimal(item.net_weight)}
                  </Text>
                </View>
              )}

              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  <Text style={styles.currencySymbol}>{currencySymbol}</Text>
                  {formatToTwoDecimal(item.unitPrice)}
                </Text>
              </View>
              {/* per-product discount */}
              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text
                  style={[styles.tableCell, { textAlign: "left", color: "red" }]}
                >
                  <Text style={styles.currencySymbol}>{currencySymbol}</Text>
                  {formatToTwoDecimal(getItemDiscountAmount(item))}
                  {item.discount_type !== "fixed" && Number(item.discount) > 0
                    ? ` (${formatToTwoDecimal(item.discount)}%)`
                    : ""}
                </Text>
              </View>
              <View
                style={[
                  styles.tableCol,
                  !status || status !== "Approved" ? styles.tableColWide : {},
                ]}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {/* {item.weight !== undefined && item.weight !== null
                    ? (
                        Math.trunc(
                          item.weight * (1 - (item.moisture || 0) / 100)
                        ) * item.unitPrice
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : ""} */}
                  {formatToTwoDecimal(
                    item.net_weight * item.unitPrice -
                      getItemDiscountAmount(item),
                  )}
                </Text>
              </View>
            </View>
          ))}

          {/* TOTALS ROW */}

          <View style={styles.tableRow}>
            <View
              style={{
                ...styles.tableCol,
                ...styles.tableHead,
                width: "33.33%",
                ...(!status || status !== "Approved"
                  ? { width: "40%" }
                  : {}),
              }}
            >
              <Text style={[styles.tableCell, { textAlign: "left" }]}>TLL</Text>
            </View>
            {/* Total Weight */}
            <View
              style={{
                ...styles.tableCol,
                ...styles.tableHead,
                ...(!status || status !== "Approved"
                  ? styles.tableColWide
                  : {}),
              }}
            >
              <Text style={[styles.tableCell, { textAlign: "left" }]}>
                {formatToTwoDecimal(totalWeightInKilo)}
              </Text>
            </View>
            {/* Total Moisture */}
            <View
              style={{
                ...styles.tableCol,
                ...styles.tableHead,
                ...(!status || status !== "Approved"
                  ? styles.tableColWide
                  : {}),
              }}
            >
              <Text style={[styles.tableCell, { textAlign: "left" }]}>
                {/* {totalMoistureInKilo.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} */}
              </Text>
            </View>
            {/* Total Net Weight */}
            <View
              style={{
                ...styles.tableCol,
                ...styles.tableHead,
                ...(!status || status !== "Approved"
                  ? styles.tableColWide
                  : {}),
              }}
            >
              <Text style={[styles.tableCell, { textAlign: "left" }]}>
                {formatToTwoDecimal(totalNetWeightInKilo)}
              </Text>
            </View>
            {/* Total Returned Qty */}
            {status === "Approved" && (
              <View
                style={{
                  ...styles.tableCol,
                  ...styles.tableHead,
                }}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {formatToTwoDecimal(totalReturnedQty)}
                </Text>
              </View>
            )}
            {/* Total T.N.W */}
            {status === "Approved" && (
              <View
                style={{
                  ...styles.tableCol,
                  ...styles.tableHead,
                }}
              >
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {formatToTwoDecimal(totalNetWeightInKilo - totalReturnedQty)}
                </Text>
              </View>
            )}
            {/* Total Unit Price */}
            <View
              style={{
                ...styles.tableCol,
                ...styles.tableHead,
                ...(!status || status !== "Approved"
                  ? styles.tableColWide
                  : {}),
              }}
            >
              <Text style={[styles.tableCell, { textAlign: "left" }]}>
                {/* {totalUnitPrice} */}
              </Text>
            </View>
            {/* Total Discount */}
            <View
              style={{
                ...styles.tableCol,
                ...styles.tableHead,
                ...(!status || status !== "Approved"
                  ? styles.tableColWide
                  : {}),
              }}
            >
              <Text
                style={[styles.tableCell, { textAlign: "left", color: "red" }]}
              >
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(totalItemDiscount)}
              </Text>
            </View>
            {/* Total of Subtotal */}
            <View
              style={{
                ...styles.tableCol,
                ...styles.tableHead,
                ...(!status || status !== "Approved"
                  ? styles.tableColWide
                  : {}),
              }}
            >
              <Text style={[styles.tableCell, { textAlign: "left" }]}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {/* {totalNetWeight.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} */}
                {formatToTwoDecimal(
                  totalNetWeight - totalReturnedAmount - totalItemDiscount,
                )}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.totalContainer}>
          <View style={styles.totalCard}>
            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Total Qty:</Text>
              <Text style={styles.summaryValue}>
                {formatToTwoDecimal(totalWeightInKilo)} Kg
              </Text>
            </View>

            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Total Moisture:</Text>
              <Text style={{ ...styles.summaryValue, color: "red" }}>
                {formatToTwoDecimal(totalMoistureInKilo)} Kg
              </Text>
            </View>

            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Total Net Qty:</Text>
              <Text style={{ ...styles.summaryValue, color: "red" }}>
                {formatToTwoDecimal(totalNetWeightInKilo)} Kg
              </Text>
            </View>

            <CommissionPDFSection
              commissions={commissions}
              currencySymbol={currencySymbol}
              styles={styles}
            />
          </View>
          <View style={styles.totalCardRightSide}>
            {/* Total Qty Amount */}
            <View style={styles.summaryColumn}>
              <Text style={styles.summaryLabel}>Total Qty Amount</Text>
              <Text style={styles.summaryValue}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(totalWeighted)}
              </Text>
            </View>

            {/* Total Amount of Moisture */}
            <View style={styles.summaryColumn}>
              <Text style={styles.summaryLabel}>Total Amount of Moisture</Text>
              <Text style={{ ...styles.summaryValue, color: "red" }}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(totalMoisture)}
              </Text>
            </View>

            {/* Total Net Qty Amount */}
            <View style={styles.summaryColumn}>
              <Text style={styles.summaryLabel}>Total Net Qty Amount</Text>
              <Text style={{ ...styles.summaryValue, color: "red" }}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(productSubtotal)}
              </Text>
            </View>

            {/* Total Product Discount (sum of per-product discounts) */}
            <View style={styles.summaryColumn}>
              <Text style={styles.summaryLabel}>Total Product Discount</Text>
              <Text style={{ ...styles.summaryValue, color: "red" }}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(totalItemDiscount)}
              </Text>
            </View>

            {/* Subtotal (net qty amount less per-product discounts) */}
            <View style={styles.summaryColumn}>
              <Text style={styles.summaryLabel}>Subtotal</Text>
              <Text style={styles.summaryValue}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(discountedSubtotal)}
              </Text>
            </View>

            {/* Discount */}
            <View style={styles.summaryColumn}>
              <Text style={styles.summaryLabel}>
                Discount (
                {payableDiscountType === "currency"
                  ? formatToTwoDecimal(discountCalculation)
                  : `${discountValue}%`}
                )
              </Text>
              <Text style={{ ...styles.summaryValue, color: "red" }}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(discountCalculation)}
              </Text>
            </View>

            {/* Gross Amount */}
            <View style={styles.summaryColumn}>
              <Text style={styles.summaryLabel}>Gross Amount</Text>
              <Text style={styles.summaryValue}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(grossAmount)}
              </Text>
            </View>

            {isSpecialTaxApplied ? (
              <View style={styles.summaryColumn}>
                <Text style={{ ...styles.summaryLabel, color: "#198754" }}>
                  Tax (Special Computation)
                </Text>
                <Text style={{ ...styles.summaryValue, color: "#198754" }}>
                  <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                  {formatToTwoDecimal(grossAmount * 0.1088)}
                </Text>
              </View>
            ) : (
              <>
                {/* VAT (green) */}
                <View style={styles.summaryColumn}>
                  <Text style={{ ...styles.summaryLabel, color: "#198754" }}>
                    {parseFloat(vatPercent) > 0
                      ? `VAT (${vatPercent}%)`
                      : "Non-VAT"}
                  </Text>
                  <Text style={{ ...styles.summaryValue, color: "#198754" }}>
                    <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                    {formatToTwoDecimal(vatAmount)}
                  </Text>
                </View>

                {/* Withholding Tax */}
                {parseFloat(taxRate) > 0 && (
                  <View style={styles.summaryColumn}>
                    <Text style={{ ...styles.summaryLabel, color: "red" }}>
                      Withholding Tax ({taxRate}%)
                    </Text>
                    <Text style={{ ...styles.summaryValue, color: "red" }}>
                      ({formatToTwoDecimal(taxAmount)})
                    </Text>
                  </View>
                )}
              </>
            )}

            {/* Weighing Fee (Non-Taxable) */}
            <View style={styles.summaryColumn}>
              <Text style={styles.summaryLabel}>
                Weighing Fee (Non-Taxable)
              </Text>
              <Text style={{ ...styles.summaryValue, color: "red" }}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(weighingFee)}
              </Text>
            </View>

            {/* Other Fees (Non-Taxable) */}
            {fetchPayableFees.map((fee, index) => (
              <View key={index} style={styles.summaryColumn}>
                <Text style={styles.summaryLabel}>
                  {fee.fee_name} (Non-Taxable)
                </Text>
                <Text style={{ ...styles.summaryValue, color: "red" }}>
                  <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                  {formatToTwoDecimal(fee.fee_amount)}
                </Text>
              </View>
            ))}

            {/* Returned Amount (Approved only) */}
            {status === "Approved" && totalReturnedAmount > 0 && (
              <View style={styles.summaryColumn}>
                <Text style={{ ...styles.summaryLabel, color: "red" }}>
                  Returned Amount
                </Text>
                <Text style={{ ...styles.summaryValue, color: "red" }}>
                  ({formatToTwoDecimal(totalReturnedAmount)})
                </Text>
              </View>
            )}

            {/* Total Amount */}
            <View style={styles.summaryColumn}>
              <Text style={[styles.summaryLabel, { fontWeight: "bold" }]}>
                Payable Amount
              </Text>
              <Text
                style={{
                  ...styles.summaryValue,
                  color: "red",
                  fontWeight: "bold",
                }}
              >
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(
                  isReOpenAdded ? parseFloat(totalPrice || 0) : totalAmount,
                )}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>{transaction_id}</Text>
          <Text style={styles.footerText}>{today}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default PayablePDFDocument;
